/**
 * AMPD DCMS2 — Avatar image fallback (+ opt-in `data-dcms-img-fallback`)
 *
 * The Avatar component (views/UI/Components/tailwind/Avatar/avatar.tpl) renders
 * the photo as `<img>` followed by the same initials badge an avatar without a
 * photo shows — only hidden with the `hidden` attribute. When the photo fails to
 * load (404/403, a removed file, a host that does not resolve), this module
 * removes the `<img>` and un-hides the badge, so the avatar ends up with exactly
 * the DOM of an avatar rendered without `src`.
 *
 * Any other image can opt in with the `data-dcms-img-fallback` attribute (the
 * user photo in the topbar and in the dashboard welcome widget do): when it fails
 * to load, the `<img>` is removed and the element right after it — if it carries
 * `hidden` — is shown. The template renders there, hidden, the placeholder it
 * shows without a photo, so after the error the markup is the one without a
 * photo. With no hidden element right after it the image is only removed.
 *
 * `error` does not bubble, so the listener sits on the document in the capture
 * phase: it covers images added later (AJAX, DCMSAvatar, modals cloned into this
 * document) without wiring every element. main.js runs as a module, i.e. after
 * the document is parsed, so a photo that failed before that is caught by the
 * scan at the bottom instead.
 *
 * Avatar markup without the hidden badge (HTML cached from an older template) is
 * left as it is. Images rendered by React (the @dcms2/react-ui Avatar on a page
 * that also loads main.js) are skipped: the component handles the error itself,
 * and a node removed behind React's back makes its next render throw
 * ("removeChild … not a child of this node") and unmount the tree.
 */

/** The photo of an Avatar component. */
const AVATAR_IMAGE_SELECTOR = '.dcms-avatar > img';

/** Any image that opted in to the fallback. */
const OPT_IN_IMAGE_SELECTOR = 'img[data-dcms-img-fallback]';

/** Every image this module handles. */
const IMAGE_SELECTOR = `${AVATAR_IMAGE_SELECTOR}, ${OPT_IN_IMAGE_SELECTOR}`;

/** The hidden initials badge next to the photo, relative to the avatar element. */
const INITIALS_SELECTOR = ':scope > span[hidden][aria-hidden="true"]';

/**
 * Whether React rendered the element — it carries React's internal fiber key
 * (`__reactFiber$…` since React 17, `__reactInternalInstance$…` before).
 *
 * @param {Element} el
 * @returns {boolean}
 */
function isRenderedByReact(el) {
    return Object.keys(el).some((key) => key.startsWith('__reactFiber$') || key.startsWith('__reactInternalInstance$'));
}

/**
 * Replaces an image that failed to load with its fallback: the initials badge
 * of an Avatar, or the hidden element right after an opted-in image.
 * Safe to call more than once for the same image.
 *
 * @param {HTMLImageElement} img
 */
function showFallback(img) {
    if (isRenderedByReact(img)) {
        return;
    }
    if (img.matches(OPT_IN_IMAGE_SELECTOR)) {
        const next = img.nextElementSibling;
        if (next?.hasAttribute('hidden')) {
            next.hidden = false;
        }
        img.remove();
        return;
    }
    const initials = img.parentElement?.querySelector(INITIALS_SELECTOR);
    if (!initials) {
        return;
    }
    initials.hidden = false;
    img.remove();
}

// Duck-typed rather than `instanceof Element`: a node cloned in an iframe and
// adopted into this document keeps the prototypes of the iframe's realm.
document.addEventListener('error', (event) => {
    const target = event.target;
    if (typeof target?.matches === 'function' && target.matches(IMAGE_SELECTOR)) {
        showFallback(target);
    }
}, true);

// Images that failed before this module ran: loading is over, yet there are no
// pixels. naturalWidth is 0 also for an SVG without an intrinsic size, so
// decode() (which rejects only for an image that cannot be decoded) decides.
for (const img of document.querySelectorAll(IMAGE_SELECTOR)) {
    if (img.complete && img.naturalWidth === 0) {
        img.decode().catch(() => {
            if (img.isConnected) {
                showFallback(img);
            }
        });
    }
}
